import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, gbp2 } from "@/lib/api";
import {
  Search, X, ChevronDown, ChevronRight, Users, Award, BarChart2, GitBranch,
  CheckCircle2, Circle, Clock, AlertCircle, ReceiptText,
} from "lucide-react";

const SIMPLE_STATUS_TABS = [
  { id: "ALL",      label: "All" },
  { id: "ACTIVE",   label: "🟢 Active" },
  { id: "AWAITING", label: "🟡 Awaiting appointment" },
  { id: "INACTIVE", label: "⚪ Inactive" },
];

const EXPIRY_FILTERS = [
  { id: "30",      label: "Expiring in 30 days" },
  { id: "90",      label: "Expiring in 3 months" },
  { id: "180",     label: "Expiring in 6 months" },
  { id: "all",     label: "All active credits" },
  { id: "expired", label: "Expired" },
  { id: "used",    label: "Used" },
  { id: "pending", label: "Pending" },
];

/**
 * Admin → Clients → Referrals — the primary CRM/analytics dashboard.
 *
 * Primary view (default): **By Referrer** — one collapsed row per client
 * with an expand arrow that reveals every referral they've generated
 * plus their credit ledger. This is the interface the admin will use
 * daily.
 *
 * Secondary views: Transactions (raw booking-level list), Analytics,
 * Top Referrers, Network.
 */
export default function AdminReferrals() {
  const [view, setView] = useState("by-referrer");
  const [summary, setSummary] = useState(null);

  const loadSummary = async () => {
    try {
      const r = await api.get("/admin/referrals");
      setSummary(r.data.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load referrals");
    }
  };
  useEffect(() => { loadSummary(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-referrals">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="mono-label">Clients</p>
          <h2 className="font-serif text-3xl text-stone-900">Referral Management</h2>
          <p className="text-xs text-stone-500 mt-1">
            Two separate financial transactions per referral: <strong>discount</strong> off the referred client's booking + <strong>credit</strong> earned by the referrer once the appointment completes.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ViewTab id="by-referrer"  active={view} setActive={setView} icon={<Users size={12} />}>By Referrer</ViewTab>
          <ViewTab id="expiring"     active={view} setActive={setView} icon={<Clock size={12} />}>Expiring Soon</ViewTab>
          <ViewTab id="transactions" active={view} setActive={setView} icon={<ReceiptText size={12} />}>Transactions</ViewTab>
          <ViewTab id="analytics"    active={view} setActive={setView} icon={<BarChart2 size={12} />}>Analytics</ViewTab>
          <ViewTab id="top"          active={view} setActive={setView} icon={<Award size={12} />}>Top Referrers</ViewTab>
          <ViewTab id="tree"         active={view} setActive={setView} icon={<GitBranch size={12} />}>Network</ViewTab>
        </div>
      </div>

      {summary && <SummaryTiles s={summary} />}

      {view === "by-referrer"  && <ByReferrerView />}
      {view === "expiring"     && <ExpiringView />}
      {view === "transactions" && <TransactionsView />}
      {view === "analytics"    && <AnalyticsView />}
      {view === "top"          && <TopReferrersView />}
      {view === "tree"         && <NetworkView />}
    </div>
  );
}

/* ── Headline tiles (§11 of spec) ──────────────────────────────── */
function SummaryTiles({ s }) {
  return (
    <div className="space-y-3" data-testid="referral-summary">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Tile label="TOTAL REFERRAL BOOKINGS" value={s.total_referral_bookings}      testid="tile-total-bookings" />
        <Tile label="COMPLETED"               value={s.completed_referrals}          testid="tile-completed" />
        <Tile label="PENDING"                 value={s.pending_referrals}            testid="tile-pending" />
        <Tile label="DISCOUNTS GIVEN"         value={gbp2(s.total_referral_discounts_given)} testid="tile-discounts-given" sub="off referred client bookings" />
        <Tile label="CREDITS EARNED"          value={gbp2(s.total_referral_credits_earned)}  testid="tile-credits-earned"  sub="by referrers" />
        <Tile label="PROGRAMME VALUE"         value={gbp2(s.total_programme_value)}          testid="tile-programme-value" sub="discounts + credits" gold />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="ACTIVE CLIENT CREDIT"  value={gbp2(s.active_client_credit)}   testid="tile-active-credit"  />
        <Tile label="USED CLIENT CREDIT"    value={gbp2(s.used_client_credit)}     testid="tile-used-credit"    />
        <Tile label="PENDING CLIENT CREDIT" value={gbp2(s.pending_client_credit)}  testid="tile-pending-credit" />
        <Tile label="CLIENTS WITH CODES"    value={s.clients_with_codes}           testid="tile-clients-codes"  />
      </div>
    </div>
  );
}

/* ── Primary: by-referrer expandable table ─────────────────────── */
function ByReferrerView() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/referrals/by-referrer");
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load referrers");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) =>
      (r.referrer_name || "").toLowerCase().includes(n) ||
      (r.referrer_email || "").toLowerCase().includes(n) ||
      (r.referrer_code || "").toLowerCase().includes(n)
    );
  }, [rows, q]);
  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  return (
    <>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center border hairline bg-white px-3 py-2 flex-1 min-w-[220px]">
          <Search size={14} className="text-stone-500 mr-2" />
          <input
            data-testid="by-referrer-search"
            placeholder="Search by name, email, or code"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 text-sm focus:outline-none"
          />
        </div>
      </div>
      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 mono-label text-[10px]">
            <tr>
              <Th className="w-8"></Th>
              <Th>Client</Th>
              <Th>Referral Code</Th>
              <Th className="text-right">Referrals</Th>
              <Th className="text-right">Pending</Th>
              <Th className="text-right">Active Credit</Th>
              <Th className="text-right">Used Credit</Th>
              <Th className="text-right">Total Earned</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500" data-testid="by-referrer-empty">
                No referrers yet. Rows appear once a client's referral code is used at checkout.
              </td></tr>
            )}
            {filtered.map((r) => {
              const isOpen = expanded.has(r.referrer_id);
              return (
                <React.Fragment key={r.referrer_id}>
                  <tr
                    className="border-t hairline hover:bg-stone-50 cursor-pointer"
                    onClick={() => toggle(r.referrer_id)}
                    data-testid={`by-referrer-row-${r.referrer_id}`}
                  >
                    <td className="px-4 py-3">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.referrer_name || "—"}</div>
                      <div className="text-xs text-stone-500">{r.referrer_email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.referrer_code || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{r.referrals_count}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-amber-800">{gbp2(r.pending_credit)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-emerald-800">{gbp2(r.active_credit)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-stone-500">{gbp2(r.used_credit)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs gold">{gbp2(r.total_earned)}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-stone-50/50" data-testid={`by-referrer-detail-${r.referrer_id}`}>
                      <td colSpan={8} className="px-4 py-4">
                        <ExpandedReferrer row={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ExpandedReferrer({ row }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    api.get(`/admin/referrals/ledger/${row.referrer_id}`)
      .then((r) => setLedger(r.data))
      .catch(() => setLedger({ entries: [] }));
  }, [row.referrer_id]);
  return (
    <div className="space-y-5">
      <div>
        <p className="mono-label text-stone-500 mb-2">
          PEOPLE {row.referrer_name?.toUpperCase() || "THIS CLIENT"} HAS REFERRED · {row.referrals.length}
        </p>
        <div className="border hairline bg-white overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-stone-100 text-stone-500 mono-label text-[10px]">
              <tr>
                <Th>Referred Client</Th>
                <Th>Booking</Th>
                <Th>Appointment</Th>
                <Th>Attended?</Th>
                <Th className="text-right">Client Discount</Th>
                <Th className="text-right">Referrer Credit</Th>
                <Th>Credit Status</Th>
                <Th>Expires</Th>
                <Th>Warning</Th>
              </tr>
            </thead>
            <tbody>
              {row.referrals.map((r) => (
                <tr key={r.id} className="border-t hairline">
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.referred_client_name || "—"}</div>
                    <div className="text-[10px] text-stone-500">{r.referred_client_email}</div>
                  </td>
                  <td className="px-4 py-2 font-mono whitespace-nowrap">{r.booking_reference}</td>
                  <td className="px-4 py-2">{fmtDate(r.appointment_date)}<div className="text-[10px] text-stone-500">{r.appointment_status || "—"}</div></td>
                  <td className="px-4 py-2">{r.attended ? <span className="text-emerald-700">Yes</span> : <span className="text-stone-500">Not yet</span>}</td>
                  <td className="px-4 py-2 text-right font-mono">− {gbp2(r.referral_discount)}</td>
                  <td className="px-4 py-2 text-right font-mono gold">{gbp2(r.referral_credit)}</td>
                  <td className="px-4 py-2"><CreditPill state={r.credit_state} amount={r.referral_credit} /></td>
                  <td className="px-4 py-2 text-[11px]">
                    {r.expires_at ? (
                      <>
                        <div>{fmtDate(r.expires_at)}</div>
                        {r.days_remaining != null && <div className="text-[10px] text-stone-500">{r.days_remaining} days left</div>}
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2"><ExpiryPill state={r.expiry_state || "none"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {ledger && ledger.entries?.length > 0 && (
        <div data-testid={`ledger-${row.referrer_id}`}>
          <p className="mono-label text-stone-500 mb-2">CREDIT LEDGER · {gbp2(row.total_earned)} EARNED</p>
          <div className="border hairline bg-white overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-stone-100 text-stone-500 mono-label text-[10px]">
                <tr>
                  <Th>Date</Th>
                  <Th>Source</Th>
                  <Th>Type</Th>
                  <Th className="text-right">Amount</Th>
                  <Th>Status</Th>
                  <Th>Activated</Th>
                  <Th>Used</Th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((e) => (
                  <tr key={e.id} className="border-t hairline">
                    <td className="px-4 py-2">{fmtDate(e.created_at)}</td>
                    <td className="px-4 py-2">{e.referred_client_name || "—"}</td>
                    <td className="px-4 py-2">Referral credit</td>
                    <td className="px-4 py-2 text-right font-mono gold">{gbp2(e.amount)}</td>
                    <td className="px-4 py-2">
                      <span className={`mono-label text-[10px] border px-2 py-0.5 ${LEDGER_STATUS[e.status]?.cls || ""}`}>
                        {LEDGER_STATUS[e.status]?.label || e.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-2">{e.activated_at ? fmtDate(e.activated_at) : "—"}</td>
                    <td className="px-4 py-2">{e.used_at ? fmtDate(e.used_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const LEDGER_STATUS = {
  pending:     { cls: "bg-amber-50 text-amber-800 border-amber-200",         label: "PENDING" },
  active:      { cls: "bg-emerald-50 text-emerald-800 border-emerald-200",   label: "ACTIVE" },
  used:        { cls: "bg-stone-100 text-stone-600 border-stone-200",        label: "USED" },
  invalidated: { cls: "bg-red-50 text-red-700 border-red-200",               label: "INVALIDATED" },
  expired:     { cls: "bg-stone-200 text-stone-700 border-stone-300",        label: "EXPIRED" },
};

const EXPIRY_PILL = {
  normal:        { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "NORMAL" },
  approaching:   { cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "APPROACHING" },
  expiring_soon: { cls: "bg-red-50 text-red-700 border-red-200",             label: "EXPIRING SOON" },
  expired:       { cls: "bg-stone-200 text-stone-700 border-stone-300",      label: "EXPIRED" },
  none:          { cls: "bg-stone-50 text-stone-400 border-stone-200",       label: "—" },
};

/* ── Expiring credits view — the "Credit Expiring Soon" panel ─────── */
function ExpiringView() {
  const [within, setWithin] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/referrals/expiring`, { params: { within } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load expiring credits"); }
    setLoading(false);
  }, [within]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="expiring-view">
      <div className="flex flex-wrap gap-2">
        {EXPIRY_FILTERS.map((f) => (
          <button key={f.id} data-testid={`expiry-filter-${f.id}`} onClick={() => setWithin(f.id)}
                  className={`mono-label text-[10px] px-3 py-2 border ${within === f.id ? "bg-stone-900 text-white border-stone-900" : "hairline bg-white text-stone-600 hover:border-stone-900"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 mono-label text-[10px]">
            <tr>
              <Th>Client</Th>
              <Th>Referral Source</Th>
              <Th className="text-right">Credit</Th>
              <Th>Status</Th>
              <Th>Activated</Th>
              <Th>Expiry Date</Th>
              <Th className="text-right">Days remaining</Th>
              <Th>Warning</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500">Loading…</td></tr>}
            {!loading && data?.rows?.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500" data-testid="expiring-empty">
                No credits in this window.
              </td></tr>
            )}
            {(data?.rows || []).map((r) => (
              <tr key={r.ledger_id} className="border-t hairline hover:bg-stone-50" data-testid={`expiring-row-${r.ledger_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.client_name}</div>
                  <div className="text-xs text-stone-500">{r.client_email}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm">{r.referred_client_name || "—"}</div>
                  {r.referral_code && <div className="text-[10px] text-stone-500 font-mono">{r.referral_code}</div>}
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs gold">{gbp2(r.amount)}</td>
                <td className="px-4 py-3">
                  <span className={`mono-label text-[10px] border px-2 py-0.5 ${LEDGER_STATUS[r.status]?.cls || ""}`}>
                    {LEDGER_STATUS[r.status]?.label || r.status?.toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{fmtDate(r.activated_at)}</td>
                <td className="px-4 py-3 text-xs">{fmtDate(r.expires_at)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{r.days_remaining ?? "—"}</td>
                <td className="px-4 py-3"><ExpiryPill state={r.expiry_state || "none"} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpiryPill({ state }) {
  const m = EXPIRY_PILL[state] || EXPIRY_PILL.none;
  return <span className={`mono-label text-[10px] border px-2 py-1 ${m.cls}`} data-testid={`expiry-pill-${state}`}>{m.label}</span>;
}

/* ── Secondary: raw transaction list (kept for admins who prefer it) ── */
function TransactionsView() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "ALL") params.status = status;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/admin/referrals", { params });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load referrals");
    }
    setLoading(false);
  }, [status, q]);
  // Auto-reload on status change; search submits on Enter via the input.
  useEffect(() => { load(); }, [status, load]);
  const awaiting = useMemo(
    () => rows
      .filter((r) => r.simple_status === "AWAITING")
      .sort((a, b) => (a.appointment_date || "").localeCompare(b.appointment_date || "")),
    [rows]
  );
  return (
    <>
      {awaiting.length > 0 && (
        <div className="border hairline bg-amber-50/50 p-4" data-testid="action-needed">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle size={16} className="text-amber-700" />
            <p className="mono-label text-amber-900">
              ACTION NEEDED · {awaiting.length} REFERRAL{awaiting.length > 1 ? "S" : ""} AWAITING APPOINTMENT
            </p>
          </div>
          <div className="space-y-2">
            {awaiting.slice(0, 8).map((r) => (
              <div key={r.id} className="bg-white border hairline px-4 py-3 flex flex-wrap gap-3 items-center justify-between cursor-pointer hover:border-amber-400"
                   onClick={() => setSelected(r.id)} data-testid={`action-row-${r.id}`}>
                <div className="text-sm">
                  <span className="font-medium">{r.referrer_name || "—"}</span>
                  <span className="text-stone-400 mx-2">→</span>
                  <span className="font-medium">{r.referred_client_name || "—"}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-stone-600">
                  <span className="flex items-center gap-1"><Clock size={12} /> {fmtDate(r.appointment_date)}</span>
                  <span className="mono-label gold">{gbp2(r.referral_credit)} PENDING</span>
                </div>
              </div>
            ))}
            {awaiting.length > 8 && <p className="text-xs text-stone-500 mt-2">+ {awaiting.length - 8} more</p>}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        {SIMPLE_STATUS_TABS.map((t) => (
          <button key={t.id} data-testid={`filter-${t.id.toLowerCase()}`}
                  onClick={() => setStatus(t.id)}
                  className={`mono-label text-[11px] px-4 py-2.5 border ${status === t.id ? "bg-stone-900 text-white border-stone-900" : "hairline bg-white text-stone-700 hover:border-stone-900"}`}>
            {t.label}
          </button>
        ))}
        <div className="flex items-center border hairline bg-white px-3 py-2 flex-1 min-w-[220px] ml-auto">
          <Search size={14} className="text-stone-500 mr-2" />
          <input data-testid="referral-search" placeholder="Search referrer, referred client, or code"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()} className="flex-1 text-sm focus:outline-none" />
        </div>
      </div>

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 mono-label text-[10px]">
            <tr>
              <Th>Referrer</Th>
              <Th>Referred client</Th>
              <Th>Status</Th>
              <Th className="text-right">Client Discount</Th>
              <Th className="text-right">Referrer Credit</Th>
              <Th>Credit</Th>
              <Th>Appointment</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500" data-testid="referral-empty">
                No referrals yet. Records appear as clients apply another client's referral code at checkout.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t hairline hover:bg-stone-50" data-testid={`referral-row-${r.id}`}>
                <td className="px-4 py-3"><div className="font-medium">{r.referrer_name || "—"}</div><div className="text-xs text-stone-500">{r.referrer_email}</div></td>
                <td className="px-4 py-3"><div className="font-medium">{r.referred_client_name || "—"}</div><div className="text-xs text-stone-500 font-mono">{r.referral_code}</div></td>
                <td className="px-4 py-3"><SimpleStatusPill status={r.simple_status} /></td>
                <td className="px-4 py-3 text-right font-mono text-xs">− {gbp2(r.referral_discount)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs gold">{gbp2(r.referral_credit)}</td>
                <td className="px-4 py-3"><CreditPill state={r.credit_state} amount={r.referral_credit} /></td>
                <td className="px-4 py-3 text-xs">{fmtDate(r.appointment_date)}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`referral-view-${r.id}`} onClick={() => setSelected(r.id)}
                          className="mono-label text-stone-600 hover:text-stone-900">View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <ReferralDetail bookingId={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

/* ── Shared sub-components ─────────────────────────────────────── */
function ViewTab({ id, active, setActive, icon, children }) {
  const on = active === id;
  return (
    <button data-testid={`referral-tab-${id}`} onClick={() => setActive(id)}
            className={`mono-label text-[10px] px-3 py-2 border flex items-center gap-2 ${on ? "bg-stone-900 text-white border-stone-900" : "hairline bg-white text-stone-600 hover:text-stone-900"}`}>
      {icon}{children}
    </button>
  );
}
function Tile({ label, value, testid, sub, gold: goldBg }) {
  return (
    <div className={`border hairline px-4 py-3 ${goldBg ? "bg-amber-50/50" : "bg-white"}`} data-testid={testid}>
      <p className="mono-label text-stone-500 text-[10px]">{label}</p>
      <p className={`font-serif text-2xl mt-1 ${goldBg ? "gold" : ""}`}>{value}</p>
      {sub && <p className="text-[10px] text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}
function Th({ children, className = "" }) { return <th className={`text-left px-4 py-3 ${className}`}>{children}</th>; }
function StatusPill({ status }) {
  const cls = {
    ACTIVE:    "bg-amber-50 text-amber-800 border-amber-200",
    PENDING:   "bg-stone-50 text-stone-700 border-stone-200",
    QUALIFIED: "bg-emerald-50 text-emerald-800 border-emerald-200",
    COMPLETED: "bg-emerald-100 text-emerald-900 border-emerald-300",
    CANCELLED: "bg-red-50 text-red-700 border-red-200",
  }[status] || "bg-stone-50 text-stone-600 border-stone-200";
  return <span className={`mono-label text-[10px] border px-2 py-0.5 ${cls}`}>{status}</span>;
}
function SimpleStatusPill({ status }) {
  const meta = {
    ACTIVE:   { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "🟢 ACTIVE" },
    AWAITING: { cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "🟡 AWAITING APPOINTMENT" },
    INACTIVE: { cls: "bg-stone-100 text-stone-600 border-stone-200",      label: "⚪ INACTIVE" },
  }[status] || { cls: "bg-stone-50 text-stone-600 border-stone-200", label: status };
  return <span className={`mono-label text-[10px] border px-2 py-1 ${meta.cls}`} data-testid={`simple-status-${status}`}>{meta.label}</span>;
}
function CreditPill({ state, amount }) {
  const meta = {
    AVAILABLE:   { cls: "bg-emerald-100 text-emerald-900 border-emerald-300", label: `${gbp2(amount)} ACTIVE` },
    PENDING:     { cls: "bg-amber-50 text-amber-800 border-amber-200",         label: `${gbp2(amount)} PENDING` },
    USED:        { cls: "bg-stone-100 text-stone-500 border-stone-200",        label: `${gbp2(amount)} USED` },
    INVALIDATED: { cls: "bg-red-50 text-red-700 border-red-200",               label: "INVALIDATED" },
    NONE:        { cls: "bg-stone-50 text-stone-400 border-stone-200",         label: "£0" },
  }[state] || { cls: "bg-stone-50 text-stone-600 border-stone-200", label: state };
  return <span className={`mono-label text-[10px] border px-2 py-1 inline-block whitespace-nowrap ${meta.cls}`} data-testid={`credit-state-${state}`}>{meta.label}</span>;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

/* ── Detail modal ─────────────────────────────────────────────── */
function ReferralDetail({ bookingId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/referrals/${bookingId}`).then((r) => setData(r.data)).catch(() => {});
  }, [bookingId]);
  if (!data) {
    return <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"><div className="bg-white p-6 border hairline">Loading…</div></div>;
  }
  const { referral, timeline } = data;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="referral-detail">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto border hairline">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div>
            <p className="mono-label">Referral · {referral.booking_reference}</p>
            <p className="text-xs text-stone-500">{referral.referral_code}</p>
          </div>
          <button data-testid="referral-detail-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Info label="REFERRER"        value={referral.referrer_name || "—"} sub={referral.referrer_email} />
            <Info label="REFERRED CLIENT" value={referral.referred_client_name || "—"} sub={referral.referred_client_email} />
            <Info label="TREATMENT"       value={referral.treatment} />
            <Info label="BOOKING VALUE"   value={gbp2(referral.booking_value)} />
            <Info label="CLIENT DISCOUNT" value={<span className="gold">− {gbp2(referral.referral_discount)}</span>} sub="off referred booking" />
            <Info label="REFERRER CREDIT" value={<span className="gold">{gbp2(referral.referral_credit)}</span>} sub="awarded on completion" />
            <Info label="FINAL VALUE"     value={gbp2(referral.final_value)} />
            <Info label="STATUS"          value={<StatusPill status={referral.status} />} />
            <Info label="CREDIT STATE"    value={<CreditPill state={referral.credit_state} amount={referral.referral_credit} />} />
          </div>
          <div>
            <p className="mono-label mb-3">Timeline</p>
            {timeline.map((t, i) => (
              <div key={i} className="flex items-center gap-3 py-2" data-testid={`timeline-${t.stage.replace(/\s/g, "-")}`}>
                {t.reached_at ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Circle size={16} className="text-stone-300" />}
                <span className={t.reached_at ? "text-sm" : "text-sm text-stone-400"}>{t.stage}</span>
                <span className="ml-auto text-xs text-stone-500">{t.reached_at ? fmtDate(t.reached_at) : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
function Info({ label, value, sub }) {
  return (
    <div>
      <p className="mono-label text-stone-500 text-[10px]">{label}</p>
      <p className="mt-1 text-sm">{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-0.5">{sub}</p>}
    </div>
  );
}

/* ── Analytics view ───────────────────────────────────────────── */
function AnalyticsView() {
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo]   = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { period };
    if (period === "custom" && from && to) { params.date_from = from; params.date_to = to; }
    api.get("/admin/referrals/analytics", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [period, from, to]);
  if (!data) return <div className="text-stone-500 text-sm">Loading analytics…</div>;
  const maxRev = Math.max(1, ...data.buckets.map((b) => b.revenue));
  const maxRef = Math.max(1, ...data.buckets.map((b) => b.new_referrals));
  return (
    <div className="space-y-5" data-testid="referral-analytics">
      <div className="flex flex-wrap gap-2">
        {["week", "month", "year", "custom"].map((p) => (
          <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
                  className={`mono-label text-[10px] px-3 py-2 border ${period === p ? "bg-stone-900 text-white border-stone-900" : "hairline bg-white text-stone-600"}`}>
            {p.toUpperCase()}
          </button>
        ))}
        {period === "custom" && (
          <>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border hairline bg-white px-3 py-2 text-sm" data-testid="analytics-from" />
            <input type="date" value={to}   onChange={(e) => setTo(e.target.value)}   className="border hairline bg-white px-3 py-2 text-sm" data-testid="analytics-to" />
          </>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="TOTAL REFERRALS"    value={data.totals.total_referrals} />
        <Tile label="SUCCESSFUL"         value={data.totals.successful} />
        <Tile label="DISCOUNTS GIVEN"    value={gbp2(data.totals.discounts_given)} />
        <Tile label="CREDITS EARNED"     value={gbp2(data.totals.credits_earned)} />
        <Tile label="PROGRAMME VALUE"    value={gbp2(data.totals.programme_value)} gold />
        <Tile label="REFERRAL REVENUE"   value={gbp2(data.totals.referral_revenue)} />
        <Tile label="CONVERSION"         value={`${data.totals.conversion_pct}%`} />
        <Tile label="AVG REFERRED VALUE" value={gbp2(data.totals.avg_referred_value)} />
      </div>

      <div className="border hairline bg-white p-5">
        <p className="mono-label mb-3">Activity</p>
        <div className="flex items-end gap-2 h-40" data-testid="analytics-chart">
          {data.buckets.map((b, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`bucket-${b.label}`}>
              <div className="w-full flex flex-col items-center justify-end h-full">
                <div title={`Revenue ${gbp2(b.revenue)}`} style={{ height: `${(b.revenue / maxRev) * 60}%` }} className="w-3/5 bg-stone-900 min-h-[2px]" />
                <div title={`${b.new_referrals} referrals`} style={{ height: `${(b.new_referrals / maxRef) * 60}%` }} className="w-3/5 bg-amber-500/80 mt-1 min-h-[2px]" />
              </div>
              <span className="mono-label text-[9px] text-stone-500 mt-1">{b.label}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-4 mt-4 text-xs text-stone-600">
          <span className="flex items-center gap-2"><span className="w-3 h-3 bg-stone-900" /> Revenue</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 bg-amber-500/80" /> New referrals</span>
        </div>
      </div>
    </div>
  );
}

/* ── Top referrers ──────────────────────────────────────────── */
function TopReferrersView() {
  const [data, setData] = useState(null);
  const [sortKey, setSortKey] = useState("successful");
  useEffect(() => { api.get("/admin/referrals/top-referrers").then((r) => setData(r.data)).catch(() => {}); }, []);
  const rows = useMemo(() => {
    if (!data) return [];
    return [...data.rows].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  }, [data, sortKey]);
  if (!data) return <div className="text-stone-500 text-sm">Loading top referrers…</div>;
  const HeaderCell = ({ k, label }) => (
    <th className="text-right px-4 py-3 cursor-pointer" onClick={() => setSortKey(k)} data-testid={`top-sort-${k}`}>
      <span className={sortKey === k ? "gold" : ""}>{label}</span>
    </th>
  );
  return (
    <div className="border hairline bg-white overflow-x-auto" data-testid="top-referrers">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-stone-500 mono-label text-[10px]">
          <tr>
            <Th>Client</Th>
            <HeaderCell k="referrals"        label="Referrals" />
            <HeaderCell k="successful"       label="Successful" />
            <HeaderCell k="pending"          label="Pending" />
            <HeaderCell k="referred_revenue" label="Referred Revenue" />
            <HeaderCell k="referral_credit"  label="Credit Generated" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (<tr><td colSpan={6} className="px-4 py-6 text-center text-stone-500">No referrers yet.</td></tr>)}
          {rows.map((r) => (
            <tr key={r.client_id} className="border-t hairline hover:bg-stone-50">
              <td className="px-4 py-3"><div className="font-medium">{r.name}</div><div className="text-xs text-stone-500">{r.email}</div></td>
              <td className="px-4 py-3 text-right font-mono text-xs">{r.referrals}</td>
              <td className="px-4 py-3 text-right font-mono text-xs">{r.successful}</td>
              <td className="px-4 py-3 text-right font-mono text-xs">{r.pending}</td>
              <td className="px-4 py-3 text-right font-mono text-xs">{gbp2(r.referred_revenue)}</td>
              <td className="px-4 py-3 text-right font-mono text-xs gold">{gbp2(r.referral_credit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Network tree ──────────────────────────────────────────── */
function NetworkView() {
  const [tree, setTree] = useState(null);
  useEffect(() => { api.get("/admin/referrals/network/tree").then((r) => setTree(r.data.tree)).catch(() => {}); }, []);
  const flat = useMemo(() => {
    if (!tree) return [];
    const out = [];
    const walk = (node, depth) => { out.push({ node, depth }); (node.children || []).forEach((c) => walk(c, depth + 1)); };
    tree.forEach((n) => walk(n, 0));
    return out;
  }, [tree]);
  if (!tree) return <div className="text-stone-500 text-sm">Loading network…</div>;
  if (tree.length === 0) return <div className="text-stone-500 text-sm">No referral relationships yet.</div>;
  return (
    <div className="border hairline bg-white p-5 overflow-x-auto" data-testid="referral-tree">
      {flat.map(({ node, depth }, i) => (
        <div key={`${node.client_id}-${i}`} style={{ marginLeft: depth * 24 }} className="py-1">
          <div className="flex items-center gap-2">
            <span className="mono-label text-xs">{depth === 0 ? "●" : "└─"}</span>
            <span className="text-sm">{node.name}</span>
            {node.children && node.children.length > 0 && (
              <span className="mono-label text-[10px] text-stone-500">{node.children.length} referred</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
